import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Form } from 'grommet'
import { useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { FormButton, SelectInputField } from '../../../../components/common'
import RichTextEditor from '../../../../components/common/Editor2'
import { Select } from '../../../../components/common/SearchInput/Select'
import { Modal } from '../../../../components/Modals'
import { relationOptions } from '../../../../constants'
import { useFamily } from '../../../../hooks'
import { FamilyDataMock } from '../../../../types/history.type'
import { createOption } from '../../../../utils'
import { showToast } from '../../../../utils/toast'
import { familySchema } from '../../../../validation/pDirectory.schema'

type Props = {
  onClose: () => void
  defaultData?: FamilyDataMock
  id?: string
}

const EditFamily = ({ onClose, defaultData, id }: Props) => {
  const { patientId } = useParams<string>()
  const [options, setOptions] = useState<Record<string, string>[]>(
    defaultData?.attributes.medical_conditions && defaultData?.attributes.medical_conditions?.length > 0
      ? defaultData?.attributes.medical_conditions?.map(item => {
          return {
            label: item,
            value: item
          }
        })
      : []
  )
  const { updateFamily } = useFamily({ patientId: patientId || '' })
  const module = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: '' }, { align: 'center' }, { align: 'right' }],
      [{ direction: 'rtl' }],
      ['link', 'image'],
      ['clean']
    ]
  }
  const methods = useForm({
    defaultValues: {
      additional_info: defaultData?.attributes.additional_info ? defaultData?.attributes.additional_info : '',
      // full_name: defaultData?.attributes.full_name,
      medical_conditions: defaultData?.attributes.medical_conditions,
      relationship: defaultData?.attributes.relationship
    },
    resolver: yupResolver(familySchema)
  })

  const handleCreate = (inputVal: string) => {
    const newOption = createOption(inputVal)
    setOptions([...options, newOption])
  }

  const onSubmit = async (data: any) => {
    const payload = {
      data: {
        attributes: {
          ...data
        },
        type: 'family_history'
      }
    }
    try {
      await updateFamily({ formVal: payload, id: id || '' })
      showToast('Family updated successfully')
      onClose()
    } catch (error: any) {
      showToast(error?.response?.data?.errors![0]?.detail || 'an error occurred')
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit Family">
        <Form className="flex flex-col md:w-[750px] w-full" onSubmit={methods.handleSubmit(onSubmit)}>
          <Box
            className="!min-h-[auto]"
            flex={false}
            pad={{ horizontal: '32px', vertical: '16px' }}
            style={{
              overflowX: 'hidden',
              overflowY: 'auto'
            }}
            width={'100%'}
          >
            <Box
              style={{
                minHeight: '320px',
                position: 'relative'
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  width: '100%'
                }}
              >
                <Box className="md:w-[750px] w-full pb-4 mx-auto" flex={false} gap={'24px'}>
                  <Box>
                    <SelectInputField
                      label="Relationship"
                      labelKey={'name'}
                      name="relationship"
                      options={relationOptions}
                      valueKey={{ key: 'value', reduce: true }}
                    />
                  </Box>
                  {/* <Box>
                    <FormInputField label="Name" name="full_name" />
                  </Box> */}
                  <Box>
                    <Controller
                      name="medical_conditions"
                      render={({ field: { onChange, value } }) => (
                        <Select
                          getOptionLabel={({ label }) => label}
                          getOptionValue={({ value }) => value}
                          isCreatable
                          isMulti
                          label="Medical Condition"
                          onChange={selectedOptions => {
                            const values = selectedOptions ? selectedOptions?.map((option: any) => option?.value) : []
                            onChange(values) // Update the form value
                          }}
                          onCreateOption={handleCreate}
                          options={options}
                          value={options?.filter(({ value: key }) => value.includes(key))}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      control={methods.control}
                      name="additional_info"
                      render={({ field: { onChange, value } }) => (
                        <RichTextEditor label="Additional info" modules={module} onChange={onChange} value={value} />
                      )}
                    />
                  </Box>
                </Box>
              </div>
            </Box>
          </Box>
          <Box style={{ background: '#fff', bottom: 0, position: 'sticky', zIndex: 20 }}>
            <FormButton isLoading={methods.formState.isSubmitting} onClose={onClose} submit="save" />
          </Box>
        </Form>
      </Modal>
    </FormProvider>
  )
}

export default EditFamily
